import type { TableRootProps, TableScrollAreaProps } from '@chakra-ui/react';
import { Box, createSlotRecipeContext, Flex, Table as ChakraTable } from '@chakra-ui/react';
import * as React from 'react';

import { Spinner } from '../spinner';
import {
  dataTableBorderColor,
  dataTableDefaultSize,
  dataTableLoadingOverlayBg,
  dataTableLoadingSpinnerColor,
  dataTableOutlineBorderRadius,
  type DataTableSize,
} from './data-table.kds';

const { withProvider, withContext } = createSlotRecipeContext({ key: 'dataTable' });

const InlineHeaderContext = React.createContext(false);

export type DataTableRootProps = Omit<TableRootProps, 'size' | 'variant'> & {
  size?: DataTableSize;
};

const DataTableRootInner = withProvider(ChakraTable.Root, 'root') as typeof ChakraTable.Root;

const DataTableRoot = React.forwardRef<HTMLTableElement, DataTableRootProps>(function DataTableRoot(props, ref) {
  const { size = dataTableDefaultSize, ...rest } = props;
  const hasInlineHeader = React.useContext(InlineHeaderContext);

  const inlineHeaderBorderProps = hasInlineHeader
    ? { borderTopWidth: '0', borderTopLeftRadius: '0', borderTopRightRadius: '0' }
    : {};

  return <DataTableRootInner ref={ref} size={size} {...inlineHeaderBorderProps} {...rest} />;
});

const DataTableHeader = withContext(ChakraTable.Header, 'header') as typeof ChakraTable.Header;
const DataTableBody = withContext(ChakraTable.Body, 'body') as typeof ChakraTable.Body;
const DataTableRow = withContext(ChakraTable.Row, 'row') as typeof ChakraTable.Row;
const DataTableColumnHeader = withContext(ChakraTable.ColumnHeader, 'columnHeader') as typeof ChakraTable.ColumnHeader;
const DataTableCell = withContext(ChakraTable.Cell, 'cell') as typeof ChakraTable.Cell;
const DataTableFooter = withContext(ChakraTable.Footer, 'footer') as typeof ChakraTable.Footer;
const DataTableCaption = withContext(ChakraTable.Caption, 'caption') as typeof ChakraTable.Caption;

export interface DataTableScrollAreaProps extends TableScrollAreaProps {
  inlineHeader?: React.ReactNode;
}

const DataTableScrollArea = React.forwardRef<HTMLDivElement, DataTableScrollAreaProps>(
  function DataTableScrollArea(props, ref) {
    const { inlineHeader, width = 'full', whiteSpace = 'nowrap', overflowX = 'auto', position = 'relative', children, ...rest } = props;

    const [hasContent, setHasContent] = React.useState(false);
    const nodeRef = React.useRef<HTMLDivElement | null>(null);

    const boxRef = React.useCallback((node: HTMLDivElement | null) => {
      nodeRef.current = node;
      setHasContent(!!node && node.childNodes.length > 0);
    }, []);

    React.useEffect(() => {
      const node = nodeRef.current;
      if (!node) return;
      const observer = new MutationObserver(() => {
        setHasContent(node.childNodes.length > 0);
      });
      observer.observe(node, { childList: true, subtree: true });
      return () => observer.disconnect();
    }, [inlineHeader]);

    return (
      <InlineHeaderContext.Provider value={hasContent}>
        <ChakraTable.ScrollArea
          ref={ref}
          width={width}
          whiteSpace={whiteSpace}
          overflowX={overflowX}
          position={position}
          {...rest}
        >
          {inlineHeader && (
            <Box
              ref={boxRef}
              borderWidth="1px"
              borderBottomWidth="0"
              borderColor={dataTableBorderColor}
              borderTopRadius={dataTableOutlineBorderRadius}
              overflow="hidden"
              whiteSpace="normal"
              position="sticky"
              left={0}
              css={{ '&:empty': { display: 'none' } }}
            >
              {inlineHeader}
            </Box>
          )}
          {children}
        </ChakraTable.ScrollArea>
      </InlineHeaderContext.Provider>
    );
  }
);

export interface DataTableLoadingOverlayProps {
  visible?: boolean;
}

const DataTableLoadingOverlay = ({ visible = false }: Readonly<DataTableLoadingOverlayProps>) => {
  if (!visible) {
    return null;
  }

  return (
    <Flex
      data-testid="data-table-loading-overlay"
      pos="absolute"
      inset={0}
      bg={dataTableLoadingOverlayBg}
      align="center"
      justify="center"
      zIndex="100"
    >
      <Spinner size="lg" color={dataTableLoadingSpinnerColor} />
    </Flex>
  );
};

import { DataTableComposed } from './data-table-composed';
import { DataTableSkeleton } from './data-table-skeleton';
import { DataTableSortIcon } from './data-table-sort-icon';

type DataTableNamespace = typeof DataTableComposed & {
  Root: typeof DataTableRoot;
  ScrollArea: typeof DataTableScrollArea;
  Header: typeof DataTableHeader;
  Body: typeof DataTableBody;
  Row: typeof DataTableRow;
  ColumnHeader: typeof DataTableColumnHeader;
  Cell: typeof DataTableCell;
  Footer: typeof DataTableFooter;
  Caption: typeof DataTableCaption;
  Column: typeof ChakraTable.Column;
  ColumnGroup: typeof ChakraTable.ColumnGroup;
  LoadingOverlay: typeof DataTableLoadingOverlay;
  Skeleton: typeof DataTableSkeleton;
  SortIcon: typeof DataTableSortIcon;
};

export const DataTable = DataTableComposed as DataTableNamespace;
DataTable.Root = DataTableRoot;
DataTable.ScrollArea = DataTableScrollArea;
DataTable.Header = DataTableHeader;
DataTable.Body = DataTableBody;
DataTable.Row = DataTableRow;
DataTable.ColumnHeader = DataTableColumnHeader;
DataTable.Cell = DataTableCell;
DataTable.Footer = DataTableFooter;
DataTable.Caption = DataTableCaption;
DataTable.Column = ChakraTable.Column;
DataTable.ColumnGroup = ChakraTable.ColumnGroup;
DataTable.LoadingOverlay = DataTableLoadingOverlay;
DataTable.Skeleton = DataTableSkeleton;
DataTable.SortIcon = DataTableSortIcon;
